import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import i18n, { formatDateTime as formatLocaleDateTime } from '../../i18n';
import MainLayout from '../../components/common/MainLayout';
import { useGetDebugTemplatesQuery, type ScenarioTemplate } from '../../api/seasonsAdminApi';
import {
  useDryRunScenarioSpecMutation,
  usePublishScenarioSpecMutation,
} from '../../api/scenarioAdminApi';
import {
  templateToSpec,
  VALID_VISIBILITIES,
  type ScenarioBranchOutcome,
  type ScenarioDryRunResponse,
  type ScenarioQualityIssue,
  type ScenarioQualityReport,
  type ScenarioSpec,
  type ScenarioSpecGoal,
  type ScenarioVisibility,
  type SpecConversionNote,
} from '../../types/scenarioSpec';

/**
 * Мастерская авторинга сценариев.
 *
 * `POST /api/scenario-generator/publish` — единственный писатель таблицы
 * `scenario_templates`, то есть эта страница — единственная поверхность продукта,
 * через которую сценарий попадает в игровую галерею (`GET /api/scenarios`).
 * Семь канонических семян живут только в backend-сидах и в галерею не попадают,
 * пока их не прогонят здесь: семя → spec → зелёный dry-run → публикация.
 */

const { Text, Paragraph } = Typography;

const KIND_VALUES = ['tutorial', 'mechanical', 'explanatory', 'competitive', 'campaign'] as const;

const kindOptions = (t: TFunction) => KIND_VALUES.map((value) => ({
  value,
  label: t(`author.kinds.${value}`),
}));

const VISIBILITY_LABEL_KEYS: Record<ScenarioVisibility, string> = {
  draft: 'author.visibility.draft',
  private: 'author.visibility.private',
  public: 'author.visibility.public',
};

const ROLE_COLORS: Record<string, string> = {
  reference: 'blue',
  good: 'green',
  borderline: 'gold',
  bad: 'red',
};

interface HeaderValues {
  slug: string;
  title: string;
  kind: string;
  version: number;
  durationDays: number;
  startDate: Dayjs | null;
  allowRetroEdit: boolean;
  requireReferenceComparison: boolean;
}

interface GeneratorErrorData {
  message?: string;
  errors?: ScenarioQualityIssue[];
  report?: { qualityReport?: ScenarioQualityReport };
}

const createEmptySpec = (): ScenarioSpec => ({
  specVersion: 1,
  scenarioId: '',
  title: '',
  slug: '',
  kind: 'tutorial',
  version: 1,
  allowRetroEdit: true,
  metadata: { tags: [] },
  pedagogy: { learningObjectives: [], targetAudience: 'beginner', requireReferenceComparison: false },
  generationPolicy: { mode: 'sim' },
  actionsCatalog: [
    { action: 'watering', targetKinds: ['bed'], uiLabel: i18n.t('scenarios_admin:author.defaults.waterAction') },
    { action: 'fertilizing', targetKinds: ['bed'], uiLabel: i18n.t('scenarios_admin:author.defaults.fertilizeAction') },
  ],
  world: {
    startDate: '',
    timezone: 'UTC',
    durationDays: 0,
    weatherSeries: [],
    plots: [],
    beds: [],
    plantings: [],
    goals: [],
  },
  branches: [{
    branchId: 'baseline',
    label: i18n.t('scenarios_admin:author.defaults.baselineBranch'),
    role: 'reference',
    actionTape: [],
  }],
});

const toJson = (spec: ScenarioSpec): string => JSON.stringify(spec, null, 2);

const parseDay = (value?: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const specToHeader = (spec: ScenarioSpec): HeaderValues => ({
  slug: spec.slug || '',
  title: spec.title || '',
  kind: spec.kind || 'tutorial',
  version: spec.version ?? 1,
  durationDays: spec.world?.durationDays ?? 0,
  startDate: parseDay(spec.world?.startDate),
  allowRetroEdit: spec.allowRetroEdit !== false,
  requireReferenceComparison: Boolean(spec.pedagogy?.requireReferenceComparison),
});

/** `ScenarioTemplate` — то же самое семя, но с более широкими типами полей. */
const seedToSpec = (template: ScenarioTemplate) => templateToSpec({
  scenarioTemplateId: template.scenarioTemplateId,
  version: template.version,
  slug: template.slug,
  title: template.title,
  kind: template.kind,
  durationDays: template.durationDays,
  allowRetroEdit: template.allowRetroEdit,
  worldSpec: template.worldSpec as unknown as Record<string, unknown>,
  goals: (template.goals || []) as ScenarioSpecGoal[],
  scoring: template.scoring,
  narrative: template.narrative as Record<string, unknown> | undefined,
  comparison: template.comparison ?? null,
  rewards: template.rewards ?? null,
});

const formatMetric = (value: unknown): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  return formatLocaleDateTime(value) || '—';
};

const issueColumns = (t: TFunction): ColumnsType<ScenarioQualityIssue> => [
  { title: t('author.issues.code'), dataIndex: 'code', width: 210 },
  {
    title: t('author.issues.path'),
    dataIndex: 'path',
    width: 230,
    render: (path?: string) => (path ? <Text code>{path}</Text> : <Text type="secondary">—</Text>),
  },
  { title: t('author.issues.message'), dataIndex: 'message' },
];

const issueRowKey = (issue: ScenarioQualityIssue, index?: number) => `${index}-${issue.code}-${issue.path}`;

const ScenarioAuthorPage: React.FC = () => {
  const { t } = useTranslation('scenarios_admin');
  const [searchParams] = useSearchParams();
  const seedParam = searchParams.get('seed');

  const { data: templatesData, isFetching: templatesLoading } = useGetDebugTemplatesQuery();
  const [dryRunSpec, { isLoading: dryRunning }] = useDryRunScenarioSpecMutation();
  const [publishSpec, { isLoading: publishing }] = usePublishScenarioSpecMutation();

  const [headerForm] = Form.useForm<HeaderValues>();
  const [spec, setSpec] = useState<ScenarioSpec>(createEmptySpec);
  const [jsonText, setJsonText] = useState<string>(() => toJson(createEmptySpec()));
  const [appliedJson, setAppliedJson] = useState<string>(() => toJson(createEmptySpec()));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<ScenarioVisibility>('draft');
  const [seedSlug, setSeedSlug] = useState<string | null>(null);
  const [notes, setNotes] = useState<SpecConversionNote[]>([]);
  const [result, setResult] = useState<ScenarioDryRunResponse | null>(null);
  const [report, setReport] = useState<ScenarioQualityReport | null>(null);
  /** Гейт публикации: снимается ТОЛЬКО зелёным dry-run и падает от любой правки. */
  const [verified, setVerified] = useState(false);

  const templates = useMemo(() => templatesData?.templates || [], [templatesData]);
  const bootstrappedRef = useRef(false);
  /**
   * Поколение spec. Прогон длится секунды, и всё это время шапку/JSON/семя можно
   * менять. Без сверки поколения ответ, пришедший на СТАРЫЙ spec, снимал гейт
   * публикации для НОВОГО — и «Опубликовать» отправляло то, что никто не проверял.
   */
  const specEpochRef = useRef(0);

  const setSpecAndJson = useCallback((next: ScenarioSpec, syncForm: boolean) => {
    const text = toJson(next);
    specEpochRef.current += 1;
    setSpec(next);
    setJsonText(text);
    setAppliedJson(text);
    setJsonError(null);
    setVerified(false);
    if (syncForm) headerForm.setFieldsValue(specToHeader(next));
  }, [headerForm]);

  const loadSeed = useCallback((template: ScenarioTemplate) => {
    const converted = seedToSpec(template);
    setSeedSlug(template.slug);
    setNotes(converted.notes);
    setResult(null);
    setReport(null);
    setSpecAndJson(converted.spec, true);
  }, [setSpecAndJson]);

  useEffect(() => {
    headerForm.setFieldsValue(specToHeader(spec));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!seedParam || bootstrappedRef.current || templatesLoading) return;
    const template = templates.find((item) => item.slug === seedParam);
    if (!template) {
      if (templates.length > 0) {
        bootstrappedRef.current = true;
        message.error(t('author.messages.seedNotFound', { slug: seedParam }));
      }
      return;
    }
    bootstrappedRef.current = true;
    loadSeed(template);
  }, [seedParam, templates, templatesLoading, loadSeed, t]);

  /**
   * Разбирает текущий JSON и возвращает spec, с которым можно идти на сервер.
   * null = JSON битый: предыдущий валидный spec остаётся в состоянии нетронутым.
   */
  const resolveSpec = useCallback((): ScenarioSpec | null => {
    if (jsonText === appliedJson) return jsonError ? null : spec;
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err: unknown) {
      setJsonError((err as Error).message || t('author.json.parseFailed'));
      return null;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setJsonError(t('author.json.notObject'));
      return null;
    }
    const next = parsed as ScenarioSpec;
    specEpochRef.current += 1;
    setSpec(next);
    setAppliedJson(jsonText);
    setJsonError(null);
    setVerified(false);
    headerForm.setFieldsValue(specToHeader(next));
    return next;
  }, [jsonText, appliedJson, jsonError, spec, headerForm, t]);

  const onHeaderChange = (_changed: Partial<HeaderValues>, values: HeaderValues) => {
    // Правка шапки перезаписывает текст JSON целиком, поэтому неприменённые
    // правки в textarea нужно сначала применить. Иначе один символ в «Названии»
    // молча стирал вставленный, но ещё не применённый мир вместе с баннером
    // ошибки разбора — без сообщения и без возможности вернуть.
    const wasDirty = jsonText !== appliedJson;
    const base = wasDirty ? resolveSpec() : spec;
    if (!base) {
      headerForm.setFieldsValue(specToHeader(spec));
      message.error(t('author.messages.headerEditReverted'));
      return;
    }
    const nextSlug = (values.slug || '').trim();
    const nextVersion = Number(values.version) || 1;
    // scenarioId ведём за slug/version, только пока он выглядит производным.
    const derived = `${base.slug}_v${base.version ?? 1}`;
    const scenarioId = !base.scenarioId || base.scenarioId === derived
      ? `${nextSlug}_v${nextVersion}`
      : base.scenarioId;
    const next: ScenarioSpec = {
      ...base,
      scenarioId,
      slug: nextSlug,
      title: values.title || '',
      kind: values.kind || base.kind,
      version: nextVersion,
      allowRetroEdit: values.allowRetroEdit !== false,
      pedagogy: {
        ...base.pedagogy,
        requireReferenceComparison: Boolean(values.requireReferenceComparison),
      },
      world: {
        ...base.world,
        durationDays: Number(values.durationDays) || 0,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : '',
      },
    };
    // Пересинхроним форму только если её уже переписал resolveSpec — иначе
    // setFieldsValue на каждый символ дёргает каретку.
    setSpecAndJson(next, wasDirty);
  };

  const absorbError = (err: unknown, fallback: string) => {
    const data = (err as { data?: GeneratorErrorData })?.data;
    if (data?.report?.qualityReport) {
      setReport(data.report.qualityReport);
    } else if (data?.errors?.length) {
      setReport({ publishable: false, errors: data.errors, warnings: [] });
    }
    setVerified(false);
    message.error(data?.message || fallback);
  };

  const onDryRun = async () => {
    const current = resolveSpec();
    if (!current) {
      message.error(t('author.messages.dryRunJsonInvalid'));
      return;
    }
    const epoch = specEpochRef.current;
    try {
      const response = await dryRunSpec({ spec: current, visibility }).unwrap();
      if (epoch !== specEpochRef.current) {
        message.info(t('author.messages.dryRunStale'));
        return;
      }
      setResult(response);
      setReport(response.qualityReport);
      const publishable = Boolean(response.qualityReport?.publishable);
      setVerified(publishable);
      if (publishable) message.success(t('author.messages.dryRunGreen'));
      else message.error(t('author.messages.dryRunErrors', { n: response.qualityReport?.errors?.length ?? 0 }));
    } catch (err: unknown) {
      if (epoch !== specEpochRef.current) return;
      absorbError(err, t('author.messages.dryRunFailed'));
    }
  };

  const onPublish = async () => {
    const current = resolveSpec();
    if (!current) {
      message.error(t('author.messages.publishJsonInvalid'));
      return;
    }
    const epoch = specEpochRef.current;
    try {
      const response = await publishSpec({ spec: current, visibility }).unwrap();
      if (epoch !== specEpochRef.current) {
        message.warning(t('author.messages.publishStale'));
        return;
      }
      setResult(response);
      setReport(response.qualityReport);
      setVerified(Boolean(response.qualityReport?.publishable));
      const template = response.scenarioTemplate;
      if (response.idempotent) {
        message.success(
          t('author.messages.publishIdempotent', { slug: template.slug, version: template.version }),
        );
      } else {
        message.success(t('author.messages.published', {
          slug: template.slug,
          version: template.version,
          visibility: template.visibility,
        }));
      }
      // Черновик пишется в scenario_templates, но published_at у него пустой, а
      // каталог (GET /api/scenarios) отдаёт только опубликованные строки. Без
      // этого предупреждения публикация выглядит успешной, а список сценариев не
      // меняется вовсе — «жму опубликовать, ничего не происходит».
      if (template.visibility === 'draft') {
        message.warning(t('author.messages.draftHidden'), 8);
      }
    } catch (err: unknown) {
      if (epoch !== specEpochRef.current) return;
      absorbError(err, t('author.messages.publishFailed'));
    }
  };

  const dirtyJson = jsonText !== appliedJson;
  const canPublish = verified && !dirtyJson && !jsonError;

  const errors = useMemo(() => {
    if (!report) return [];
    if (report.errors?.length) return report.errors;
    return [
      ...(report.specValidation?.errors || []),
      ...(report.templateValidation?.errors || []),
    ];
  }, [report]);

  const warnings = useMemo(() => {
    if (!report) return [];
    if (report.warnings?.length) return report.warnings;
    return report.specValidation?.warnings || [];
  }, [report]);

  const outcomes = useMemo(() => result?.outcomes || [], [result]);

  const metricKeys = useMemo(() => {
    const keys = new Set<string>();
    outcomes.forEach((outcome) => {
      Object.keys(outcome.metrics || {}).forEach((key) => keys.add(key));
    });
    return Array.from(keys);
  }, [outcomes]);

  const issueTableColumns = useMemo(() => issueColumns(t), [t]);

  const outcomeColumns: ColumnsType<ScenarioBranchOutcome> = useMemo(() => ([
    { title: t('author.outcomes.branch'), dataIndex: 'branchId', width: 160, fixed: 'left' as const },
    {
      title: t('author.outcomes.label'),
      dataIndex: 'label',
      width: 180,
      render: (label?: string) => label || <Text type="secondary">—</Text>,
    },
    {
      title: t('author.outcomes.role'),
      dataIndex: 'role',
      width: 120,
      render: (role?: string) => (role
        ? <Tag color={ROLE_COLORS[role] || 'default'}>{role}</Tag>
        : <Text type="secondary">—</Text>),
    },
    {
      title: t('author.outcomes.terminal'),
      dataIndex: 'terminal',
      width: 110,
      render: (terminal: boolean) => (terminal
        ? <Tag color="green">{t('author.outcomes.reachedEnd')}</Tag>
        : <Tag color="orange">{t('author.outcomes.cutShort')}</Tag>),
    },
    {
      title: t('author.outcomes.goals'),
      key: 'goals',
      width: 100,
      render: (_: unknown, outcome: ScenarioBranchOutcome) => {
        const progress = outcome.goalsProgress;
        if (!progress) return <Text type="secondary">—</Text>;
        return <Text>{progress.passedCount ?? '—'}/{progress.total ?? '—'}</Text>;
      },
    },
    ...metricKeys.map((key) => ({
      title: key,
      key: `metric-${key}`,
      dataIndex: ['metrics', key],
      width: 140,
      render: (value: unknown) => formatMetric(value),
    })),
  ]), [metricKeys, t]);

  /**
   * Ограничение движка: бюджет действий (`goal.actionCount`) проверяется на живом
   * прогоне, но НЕ при завершении — в отладке цель может гореть FAIL, а в
   * сохранённом счёте PASS.
   */
  const hasActionBudgetGoal = useMemo(
    () => (spec.world?.goals || []).some((goal) => Boolean(goal.actionCount)),
    [spec],
  );

  const verdict = (() => {
    if (!report) {
      return (
        <Alert
          type="info"
          showIcon
          message={t('author.verdict.notCheckedTitle')}
          description={t('author.verdict.notCheckedDescription')}
        />
      );
    }
    if (report.publishable) {
      return (
        <Alert
          type="success"
          showIcon
          message={t('author.verdict.readyTitle')}
          description={t('author.verdict.readyDescription', {
            branches: report.summary?.branchCount ?? outcomes.length,
            warnings: warnings.length,
          })}
        />
      );
    }
    return (
      <Alert
        type="error"
        showIcon
        message={t('author.verdict.blockedTitle', { n: errors.length })}
        description={t('author.verdict.blockedDescription')}
      />
    );
  })();

  return (
    <MainLayout>
      <Row gutter={16} style={{ minHeight: '100%' }}>
        <Col span={11}>
          <Card
            title={t('author.specCard.title')}
            size="small"
            extra={<Text type="secondary" style={{ fontSize: 12 }}>publish → scenario_templates</Text>}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('author.specCard.intro')}
            />

            <Space wrap style={{ marginBottom: 12 }}>
              <Select
                style={{ minWidth: 320 }}
                placeholder={t('author.specCard.loadSeedPlaceholder')}
                value={seedSlug}
                loading={templatesLoading}
                onChange={(value: string) => {
                  const template = templates.find((item) => item.slug === value);
                  if (template) loadSeed(template);
                }}
                options={templates.map((template) => ({
                  value: template.slug,
                  label: t('author.specCard.seedOption', {
                    title: template.title || template.slug,
                    slug: template.slug,
                    kind: template.kind,
                    days: template.durationDays,
                  }),
                }))}
              />
              <Select<ScenarioVisibility>
                style={{ width: 200 }}
                value={visibility}
                onChange={(value) => setVisibility(value)}
                options={VALID_VISIBILITIES.map((item) => ({
                  value: item,
                  label: t(VISIBILITY_LABEL_KEYS[item]),
                }))}
              />
              <Button type="primary" onClick={onDryRun} loading={dryRunning}>
                {t('author.actions.dryRun')}
              </Button>
              <Popconfirm
                title={t('author.publishConfirm.title')}
                description={visibility === 'draft'
                  ? t('author.publishConfirm.descriptionDraft', { visibility: t(VISIBILITY_LABEL_KEYS[visibility]) })
                  : t('author.publishConfirm.description', { visibility: t(VISIBILITY_LABEL_KEYS[visibility]) })}
                okText={t('author.actions.publish')}
                cancelText={t('common:actions.cancel')}
                disabled={!canPublish}
                onConfirm={onPublish}
              >
                <Button disabled={!canPublish} loading={publishing}>
                  {t('author.actions.publish')}
                </Button>
              </Popconfirm>
            </Space>

            {!verified && !dirtyJson && (
              <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
                {t('author.specCard.publishLocked')}
              </Paragraph>
            )}
            {dirtyJson && (
              <Paragraph type="warning" style={{ fontSize: 12, marginBottom: 12 }}>
                {t('author.specCard.jsonDirty')}
              </Paragraph>
            )}

            {notes.map((note, index) => (
              <Alert
                key={`note-${index}`}
                type={note.severity}
                showIcon
                style={{ marginBottom: 8 }}
                message={note.message}
              />
            ))}

            <Form<HeaderValues>
              form={headerForm}
              layout="vertical"
              size="small"
              onValuesChange={onHeaderChange}
              style={{ marginTop: 8 }}
            >
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="slug" label="Slug">
                    <Input placeholder="radish-rush" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="title" label={t('author.form.title')}>
                    <Input placeholder={t('author.form.titlePlaceholder')} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="kind" label={t('author.form.kind')}>
                    <Select options={kindOptions(t)} />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="version" label={t('author.form.version')}>
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="durationDays" label={t('author.form.durationDays')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name="startDate"
                    label={t('author.form.startDate')}
                    tooltip={t('author.form.startDateTooltip')}
                  >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="allowRetroEdit" label={t('author.form.allowRetroEdit')} valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    name="requireReferenceComparison"
                    label={t('author.form.requireReference')}
                    valuePropName="checked"
                    tooltip={t('author.form.requireReferenceTooltip')}
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {jsonError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 8 }}
                message={t('author.json.notApplied')}
                description={jsonError}
              />
            )}

            <Space style={{ marginBottom: 8 }}>
              <Button size="small" onClick={() => resolveSpec()} disabled={!dirtyJson}>
                {t('author.json.apply')}
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('author.json.stats', {
                  branches: spec.branches?.length ?? 0,
                  beds: spec.world?.beds?.length ?? 0,
                  plantings: spec.world?.plantings?.length ?? 0,
                  weatherDays: spec.world?.weatherSeries?.length ?? 0,
                })}
              </Text>
            </Space>

            <Input.TextArea
              value={jsonText}
              onChange={(event) => setJsonText(event.target.value)}
              onBlur={() => resolveSpec()}
              autoSize={{ minRows: 20, maxRows: 40 }}
              spellCheck={false}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Card>
        </Col>

        <Col span={13}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {verdict}

            {hasActionBudgetGoal && (
              <Alert
                type="warning"
                showIcon
                message={t('author.actionBudgetWarning')}
              />
            )}

            <Card size="small" title={t('author.issues.errorsTitle', { n: errors.length })}>
              <Table<ScenarioQualityIssue>
                rowKey={issueRowKey}
                size="small"
                columns={issueTableColumns}
                dataSource={errors}
                pagination={false}
                scroll={{ x: 700 }}
                locale={{ emptyText: report ? t('author.issues.noErrors') : t('author.issues.notRunYet') }}
              />
            </Card>

            {warnings.length > 0 && (
              <Collapse
                size="small"
                items={[{
                  key: 'warnings',
                  label: t('author.issues.warningsTitle', { n: warnings.length }),
                  children: (
                    <Table<ScenarioQualityIssue>
                      rowKey={issueRowKey}
                      size="small"
                      columns={issueTableColumns}
                      dataSource={warnings}
                      pagination={false}
                      scroll={{ x: 700 }}
                    />
                  ),
                }]}
              />
            )}

            <Card size="small" title={t('author.outcomes.title', { n: outcomes.length })}>
              <Table<ScenarioBranchOutcome>
                rowKey={(outcome) => outcome.branchId}
                size="small"
                columns={outcomeColumns}
                dataSource={outcomes}
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: t('author.outcomes.empty') }}
              />
            </Card>

            {result?.provenance && (
              <Collapse
                size="small"
                items={[{
                  key: 'provenance',
                  label: t('author.provenance.title'),
                  children: (
                    <Descriptions size="small" column={2}>
                      <Descriptions.Item label={t('author.provenance.generator')}>
                        {result.provenance.generatorVersion || '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('author.provenance.engine')}>
                        {result.provenance.engineVersion || '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label={t('author.provenance.specHash')}>
                        <Text code>{result.provenance.sourceSpecHash || '—'}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('author.provenance.generatedAt')}>
                        {formatDateTime(result.provenance.generatedAt)}
                      </Descriptions.Item>
                    </Descriptions>
                  ),
                }]}
              />
            )}
          </Space>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default ScenarioAuthorPage;
